import type { StaffQueueCounts, StaffRole, StaffView } from "@/lib/staff-api";
import { staffCan } from "@/lib/staff-capabilities";

export interface StaffTab {
  view: StaffView;
  label: string;
  path: string;
}

/** The dashboard's tabs, in order (spec: Mark's dashboard, wireframes 1–4). */
export const STAFF_TABS: readonly StaffTab[] = [
  { view: "today", label: "Today", path: "/staff" },
  { view: "verify", label: "Verify", path: "/staff/verify" },
  { view: "process", label: "Background process", path: "/staff/process" },
  { view: "badges", label: "Badges to make", path: "/staff/badges" },
  { view: "all", label: "Employees", path: "/staff/all" },
];

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** A tab's count in plain language: what the work is, never the status name. */
export const TAB_COUNT_PHRASES: Record<keyof StaffQueueCounts, (n: number) => string> = {
  verify: (n) => plural(n, "name to verify", "names to verify"),
  process: (n) => `${n} waiting on you`,
  badges: (n) => plural(n, "badge to make", "badges to make"),
  all: (n) => plural(n, "employee", "employees"),
};

/** One link in the staff header's tab bar. */
export interface NavTab {
  key: StaffView | "companies" | "payments";
  label: string;
  path: string;
}

const PAYMENTS_TAB: NavTab = { key: "payments", label: "Payments", path: "/staff/payments" };
const COMPANIES_TAB: NavTab = { key: "companies", label: "Companies", path: "/staff/companies" };

/** The tabs a role sees, in order: the queues (Mark, IT), Payments (Accounting),
 *  then Companies (everyone). */
export function navTabsFor(role: StaffRole): NavTab[] {
  return [
    ...(staffCan(role, "work_queues") ? STAFF_TABS.map((t): NavTab => ({ key: t.view, label: t.label, path: t.path })) : []),
    ...(staffCan(role, "view_payments") ? [PAYMENTS_TAB] : []),
    ...(staffCan(role, "view_directory") ? [COMPANIES_TAB] : []),
  ];
}

export const COMPANIES_PHRASE = (n: number): string => plural(n, "company", "companies");
